import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, Eye, Landmark, Map, Mic, Scale, ScrollText, Search, Shuffle, Sparkles, Users, Vote } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { SOCIALS, ext } from "@/lib/links";
import { MysteryCarousel } from "@/components/MysteryCarousel";
import { ApiError } from "@/components/ApiError";

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");
const CAT_ICON = { "Storia e misteri": ScrollText, Personaggi: Users, "Mondo e luoghi": Map, "Frutti e poteri": Sparkles, "Governo e fazioni": Landmark, "Profezie e futuro": Eye };
const TONES = ["bg-[#E3F6FB] text-[#0E7F96]", "bg-[var(--luma-soft-purple)] text-[var(--luma-action)]", "bg-[#FBEAF7] text-[var(--luma-pink)]"];

function HomeSearch() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const active = q.trim().length >= 2;
  useEffect(() => {
    if (!active) { setRes([]); return undefined; }
    const t = setTimeout(() => api.get("/dossiers", { params: { q: q.trim() } }).then((r) => setRes(r.data)).catch(() => setRes([])), 250);
    return () => clearTimeout(t);
  }, [q, active]);
  const goAll = { pathname: "/misteri" };
  const st = { q: q.trim() };
  return (
    <form onSubmit={(e) => { e.preventDefault(); nav("/misteri", { state: st }); }} className="relative z-20 max-w-xl" role="search" data-testid="home-search-form">
      <Search size={18} className="absolute left-4 top-[15px] text-[var(--luma-muted)]" aria-hidden="true" />
      <Input data-testid="home-search-input" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Cerca misteri e argomenti"
        placeholder="Cerca un mistero, un personaggio, un argomento…" className="h-12 rounded-xl border-[var(--luma-line)] bg-white pl-11 text-base shadow-sm" />
      {active && (
        <div data-testid="home-search-results" className="absolute inset-x-0 top-full mt-2 space-y-0.5 rounded-xl border border-border bg-white p-1.5 shadow-xl">
          {res.slice(0, 5).map((d) => (
            <Link key={d.id} to={`/dossier/${d.id}`} data-testid={`home-search-result-${d.id}`} className="block rounded-lg px-3 py-2.5 text-sm hover:bg-[var(--luma-soft-purple)]">
              <span className="font-semibold">{d.titolo}</span> <span className="text-xs text-[var(--luma-muted)]">· {d.categoria}</span>
            </Link>
          ))}
          {!res.length && <p className="px-3 py-2 text-sm text-[var(--luma-muted)]">Nessun risultato.</p>}
          {res.length > 5 && <Link to={goAll} state={st} data-testid="home-search-all" className="block px-3 py-2 text-sm font-bold text-[var(--luma-action)]">Vedi tutti i {res.length} risultati</Link>}
        </div>
      )}
    </form>
  );
}

const STEPS = [[BookOpen, "Leggi", "Ogni mistero raccoglie interpretazioni diverse, spiegate per intero."], [Scale, "Confronta", "Idee dal mondo, di Lumacofono e della community, una accanto all'altra."], [Vote, "Se vuoi, scegli", "Quando una teoria ti convince, puoi sceglierla. Con calma."]];

function HowItWorks() {
  return (
    <ol className="relative hidden space-y-3 lg:block" aria-label="Come funziona">
      {STEPS.map(([Icon, t, p], i) => (
        <li key={t} className="fade-up luma-card flex items-start gap-4 p-5" style={{ animationDelay: `${120 + i * 90}ms`, marginLeft: `${i * 22}px` }}>
          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${TONES[i]}`}><Icon size={20} /></span>
          <span><b className="font-display text-lg font-extrabold">{t}</b><span className="block text-sm text-[var(--luma-muted)]">{p}</span></span>
        </li>
      ))}
    </ol>
  );
}

function Hero({ total }) {
  const nav = useNavigate();
  const surprise = () => api.get("/random").then((r) => nav(`/dossier/${r.data.id}`)).catch(() => nav("/misteri"));
  return (
    <section className="relative grid items-center gap-10 pt-4 sm:pt-10 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]" data-testid="home-hero">
      <div aria-hidden="true" className="pointer-events-none absolute -inset-x-4 -top-8 bottom-0 -z-10">
        <span className="luma-orb right-[-4rem] top-[-3rem] h-72 w-72 bg-[#E5D9FB]" />
        <span className="luma-orb left-[30%] top-[55%] h-56 w-56 bg-[#CDEFF7]/70" />
      </div>
      <div className="fade-up space-y-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[var(--luma-pink)]">One Piece · Le teorie dal mondo</p>
        <h1 className="text-[40px] font-black leading-[1.02] sm:text-6xl lg:text-[72px]">Come finirà <span className="luma-text-gradient">davvero?</span></h1>
        <p className="max-w-[640px] text-[17px] leading-relaxed text-[var(--luma-muted)] sm:text-lg" data-testid="home-intro">
          I misteri di One Piece hanno ispirato teorie da tutto il mondo. Qui puoi scoprirle, scegliere quelle che ti convincono di più o proporre la tua versione. Tanto con Oda non si sa mai.
        </p>
        <HomeSearch />
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/misteri" data-testid="explore-all-button" className="luma-btn">Esplora i misteri <ArrowRight size={17} /></Link>
          <button type="button" onClick={surprise} data-testid="surprise-button" className="luma-btn-ghost"><Shuffle size={16} /> Sorprendimi</button>
        </div>
        {total > 0 && (
          <p data-testid="mysteries-count" className="flex items-center gap-2 text-sm font-semibold text-[var(--luma-muted)]">
            <span className="h-2 w-2 rounded-full bg-[var(--luma-cyan)] shadow-[0_0_0_4px_rgba(22,184,214,.18)]" /> {total} misteri da esplorare
          </p>
        )}
      </div>
      <HowItWorks />
    </section>
  );
}

function CategoryGrid({ cats }) {
  if (!cats.length) return null;
  return (
    <section data-testid="category-grid" className="space-y-5" aria-labelledby="cat-title">
      <div>
        <p className="luma-eyebrow">Per argomento</p>
        <h2 id="cat-title" className="mt-1 text-2xl font-extrabold sm:text-[32px]">Esplora per argomento</h2>
      </div>
      <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2 md:grid-cols-3 md:gap-4">
        {cats.map((c, i) => {
          const Icon = CAT_ICON[c.nome] || ScrollText;
          return (
            <Link key={c.nome} to={`/misteri?categoria=${encodeURIComponent(c.nome)}`} data-testid={`category-link-${slug(c.nome)}`}
              className="luma-card group flex items-center gap-4 p-4 hover:-translate-y-0.5 hover:border-[#BDA4E4] sm:p-5">
              <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${TONES[i % 3]}`}><Icon size={20} /></span>
              <span className="min-w-0 flex-1">
                <span className="block font-display font-extrabold leading-snug">{c.nome}</span>
                <span className="text-xs text-[var(--luma-muted)]">{c.dossier} misteri</span>
              </span>
              <ArrowRight size={16} className="shrink-0 text-[var(--luma-action)] transition-transform group-hover:translate-x-1" />
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function PodcastCall() {
  return (
    <section data-testid="podcast-call" className="relative overflow-hidden rounded-[26px] bg-[var(--luma-night)] p-6 text-white sm:p-10">
      <span aria-hidden="true" className="luma-orb -right-16 -top-16 h-64 w-64 bg-[#7433C1]/40" />
      <span aria-hidden="true" className="luma-orb -bottom-20 left-1/3 h-56 w-56 bg-[#16B8D6]/25" />
      <div className="relative max-w-xl space-y-3">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.13em] text-[#7FDDEE]"><Mic size={14} /> Il podcast</p>
        <h2 className="text-2xl font-extrabold sm:text-[32px]">Le teorie continuano al microfono.</h2>
        <p className="text-[#C9D6E6]">Altre domande, altre idee e qualche deviazione di troppo. Ci trovi su Lumacofono.</p>
        <div className="flex flex-wrap gap-3 pt-2">
          {SOCIALS.filter((s) => ["spotify", "youtube"].includes(s.id)).map((s) => (
            <a key={s.id} href={s.url} {...ext} data-testid={`podcast-${s.id}`}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-white px-5 text-sm font-bold text-[var(--luma-ink)] transition-transform hover:-translate-y-0.5">
              Ascolta su {s.label} <span aria-hidden="true">↗</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [cats, setCats] = useState([]);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    setFailed(false);
    api.get("/categories").then((r) => setCats(r.data)).catch(() => setFailed(true));
  }, [attempt]);
  return (
    <div className="space-y-16 sm:space-y-20">
      <Hero total={cats.reduce((s, c) => s + c.dossier, 0)} />
      {failed && <ApiError testid="home-api-error" onRetry={() => setAttempt(attempt + 1)} />}
      <MysteryCarousel key={attempt} />
      <CategoryGrid cats={cats} />
      <PodcastCall />
    </div>
  );
}
